'use client';

import { RadioButton } from '@curology/ui-components-web/react/RadioButton';
import React from 'react';

import { centsToDollarsAndCents } from '@/helpers/productSelectionAndCheckout/helpers';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { type ItemPrice } from '@/types/productSelectionAndCheckout/types';
import { type PurchaseContext } from '@/types/purchaseContextToggle/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import './purchaseContextToggleStyles.css';
import { useFlowContext } from '../flow/hooks/useFlowContext';
import { usePurchaseContext } from '../product-selection/hooks/usePurchaseContext';
import { useRxPurchaseContextPrice } from '../product-selection/hooks/useRxPurchaseContextPrice';
import { useSubmittingForm } from '../product-selection/hooks/useSubmittingForm';

export interface PurchaseContextToggleProps {
  shouldCallUpdatePurchaseContextService?: boolean;
}
interface PurchaseContextButtonProps {
  purchaseType: PurchaseContext;
  purchaseContext: PurchaseContext;
  setPurchaseContext: (purchaseType: PurchaseContext) => void;
  label: string;
  originalPrice: string;
  discountedPrice: string;
  submitting: boolean;
}

const PurchaseContextButton: React.FC<PurchaseContextButtonProps> = ({
  purchaseType,
  purchaseContext,
  setPurchaseContext,
  label,
  originalPrice,
  discountedPrice,
  submitting,
}) => {
  return (
    <button
      type="button"
      className="cur-flex cur-items-center cur-w-full cur-cursor-pointer cur-outline-none cur-font-bold focus:cur-outline-none focus-visible:cur-outline focus-visible:cur-outline-2"
      onClick={() => setPurchaseContext(purchaseType)}
      disabled={submitting}
      aria-pressed={purchaseContext === purchaseType}
    >
      <RadioButton
        checked={purchaseContext === purchaseType}
        onChange={() => setPurchaseContext(purchaseType)}
        name="purchase"
        value={purchaseType}
      />
      <span className="cur-flex cur-justify-between cur-items-center cur-ml-2 cur-w-full">
        <span className="cur-text-left cur-leading-6 responsive-label-width">
          {label}
        </span>
        <span className="cur-items-end cur-inline-flex cur-flex-row cur-justify-end">
          {originalPrice !== discountedPrice && (
            <span className="cur-text-disabled cur-font-normal cur-line-through">
              {originalPrice}
            </span>
          )}
          <span className="cur-ml-2 cur-font-bold">{discountedPrice}</span>
        </span>
      </span>
    </button>
  );
};

export const PurchaseContextToggle = ({
  shouldCallUpdatePurchaseContextService,
}: PurchaseContextToggleProps) => {
  const { brand } = useFlowContext();
  const { purchaseContext, setPurchaseContext } = usePurchaseContext();
  const { submittingForm, setSubmittingForm } = useSubmittingForm();
  const { rxPurchaseContextPrices } = useRxPurchaseContextPrice();

  const handlePurchaseContextChange = async (purchaseType: PurchaseContext) => {
    setSubmittingForm(true);

    try {
      if (shouldCallUpdatePurchaseContextService) {
        await ProductSelectionAndCheckoutService.updateProductSelectionContext(
          brand,
          purchaseType,
        );
      }
      setPurchaseContext(purchaseType);
      trackEvent(AnalyticsEvent.PURCHASE_CONTEXT_TOGGLE_CLICKED, {
        purchaseOption: purchaseType,
      });
    } catch (error) {
      console.error('Failed to update purchase context');
    } finally {
      setSubmittingForm(false);
    }
  };

  const contextMapping: {
    [key: string]: { label: string; type: PurchaseContext };
  } = {
    one: { label: 'One-time purchase', type: 'onetime' },
    trial: { label: 'Subscribe and save', type: 'trial' },
    subscription: { label: 'Subscribe and save', type: 'subscription' },
  };

  const getContextDetails = (
    context: string,
  ): { label: string; type: PurchaseContext } | undefined => {
    return Object.entries(contextMapping).find(([key]) =>
      context.includes(key),
    )?.[1];
  };

  return (
    <div
      className={`cur-p-4 cur-font-body cur-bg-surface-neutral cur-mt-4 ${submittingForm ? 'cur-opacity-50' : ''}`}
    >
      {Object.keys(rxPurchaseContextPrices).map((key) => {
        const context = key;
        const contextPrices = rxPurchaseContextPrices[
          context as keyof typeof rxPurchaseContextPrices
        ] as ItemPrice;

        return (
          <div key={context}>
            <PurchaseContextButton
              purchaseType={getContextDetails(context)?.type || 'onetime'}
              purchaseContext={purchaseContext}
              setPurchaseContext={handlePurchaseContextChange}
              label={getContextDetails(context)?.label || 'Purchase'}
              originalPrice={centsToDollarsAndCents(
                contextPrices.originalAmount,
              )}
              discountedPrice={centsToDollarsAndCents(
                contextPrices.finalAmount,
              )}
              submitting={submittingForm}
            />
            {Object.keys(rxPurchaseContextPrices).indexOf(key) !==
              Object.keys(rxPurchaseContextPrices).length - 1 && (
              <div className="cur-h-[2px] cur-bg-surface-secondary cur-my-4"></div>
            )}
          </div>
        );
      })}
      <div className="cur-text-meta cur-text-left cur-text-brand cur-mt-2 cur-ml-6 cur-pl-2" />
    </div>
  );
};
